import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Tag,
  message,
  DatePicker,
  Select,
  Row,
  Col,
  InputNumber,
  Popconfirm,
  Typography,
  Tooltip,
  Dropdown,
} from "antd";
import {
  SearchOutlined,
  StopOutlined,
  UnlockOutlined,
  LockOutlined,
  FilterOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import {
  searchInfractionByFilter,
  blockInfraction,
  unblockInfraction,
  bulkBlockInfractions,
} from "../api/infractions/infractionRequest";
import { statusText, colorStatusInfraction } from "../utils/constants";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

function InfractionsManagement() {
  const [infractions, setInfractions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Filters
  const [dateRange, setDateRange] = useState([]);
  const [minDaysPassed, setMinDaysPassed] = useState(null);
  const [maxDaysPassed, setMaxDaysPassed] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchMatricule, setSearchMatricule] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const user = useSelector((state) => state.user.user);
  const workerPdfRef = useRef(null);
  const workerExcelRef = useRef(null);

  useEffect(() => {
    fetchInfractions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    workerPdfRef.current = new Worker(
      new URL("../workers/pdfWorker.js", import.meta.url)
    );
    workerExcelRef.current = new Worker(
      new URL("../workers/excelWorker.js", import.meta.url)
    );

    workerPdfRef.current.onmessage = (e) => {
      const { status, blobURL, operation, filename, message: msg } = e.data;
      switch (status) {
        case "starting":
          setIsExporting(true);
          break;
        case "generating":
          break;
        case "complete":
          if (operation === "download") {
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = filename || "file.pdf";
            link.click();
            URL.revokeObjectURL(blobURL);
          } else if (operation === "open") {
            window.open(blobURL, "_blank");
          }
          setIsExporting(false);
          break;
        case "error":
          message.error(`Erreur lors de la génération du PDF: ${msg}`);
          setIsExporting(false);
          break;
        default:
          break;
      }
    };

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error } = e.data;
      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
      } else {
        message.error(`Erreur lors de la génération du fichier Excel: ${error}`);
      }
    };

    return () => {
      workerPdfRef.current?.terminate();
      workerExcelRef.current?.terminate();
    };
  }, []);

  const fetchInfractions = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};

      if (dateRange && dateRange.length === 2) {
        filters.start_date = dateRange[0].format("YYYY-MM-DD");
        filters.end_date = dateRange[1].format("YYYY-MM-DD");
      }

      if (statusFilter) {
        filters.status = statusFilter;
      }

      if (searchMatricule) {
        filters.matricule = searchMatricule;
      }

      const data = await searchInfractionByFilter(filters);
      setInfractions(data || []);
      setSelectedRowKeys([]);
    } catch (error) {
      message.error("Erreur lors du chargement des infractions");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, statusFilter, searchMatricule]);

  const getDaysPassed = (datetimeInfraction) => {
    if (!datetimeInfraction) return null;
    const now = dayjs();
    const infDate = dayjs(datetimeInfraction);
    return now.diff(infDate, "day");
  };

  const getFilteredData = () => {
    let data = [...infractions];

    if (minDaysPassed !== null && minDaysPassed !== undefined) {
      data = data.filter((r) => {
        const days = getDaysPassed(r.datetime_infraction);
        return days !== null && days >= minDaysPassed;
      });
    }

    if (maxDaysPassed !== null && maxDaysPassed !== undefined) {
      data = data.filter((r) => {
        const days = getDaysPassed(r.datetime_infraction);
        return days !== null && days <= maxDaysPassed;
      });
    }

    return data;
  };

  const handleBlock = async (id) => {
    try {
      await blockInfraction(id);
      message.success("Infraction bloquée");
      fetchInfractions();
    } catch (error) {
      message.error(error?.response?.data?.error || "Erreur lors du blocage");
    }
  };

  const handleUnblock = async (id) => {
    try {
      await unblockInfraction(id);
      message.success("Infraction débloquée");
      fetchInfractions();
    } catch (error) {
      message.error(error?.response?.data?.error || "Erreur lors du déblocage");
    }
  };

  const handleBulkBlock = async () => {
    if (selectedRowKeys.length === 0) return;
    setBulkLoading(true);
    try {
      const result = await bulkBlockInfractions(selectedRowKeys);
      message.success(result.message);
      setSelectedRowKeys([]);
      fetchInfractions();
    } catch (error) {
      message.error(error?.response?.data?.error || "Erreur lors du blocage en masse");
    } finally {
      setBulkLoading(false);
    }
  };

  const formatDaysPassed = (days) => {
    if (days === null) return "N/A";
    if (days === 0) return <Tag color="green">Aujourd'hui</Tag>;
    if (days === 1) return <Tag color="blue">1 jour</Tag>;
    if (days <= 7) return <Tag color="cyan">{days} jours</Tag>;
    if (days <= 30) return <Tag color="orange">{days} jours</Tag>;
    if (days <= 90) return <Tag color="red">{days} jours</Tag>;
    return <Tag color="magenta">{days} jours</Tag>;
  };

  const filteredData = getFilteredData();

  // --- Export (Web Worker-based, same skeleton as other pages) ---
  const buildExportRows = (items) =>
    items.map((r) => ({
      id: r.id ?? "",
      reference: r.reference || "",
      matricule: r.matricule || "",
      type: r.type_infraction?.name || "N/A",
      amount: parseFloat(r.amount || 0),
      datetime_infraction: r.datetime_infraction
        ? dayjs(r.datetime_infraction).format("DD/MM/YYYY HH:mm")
        : "N/A",
      days_passed: getDaysPassed(r.datetime_infraction) ?? "",
      status: statusText(r.status),
      zone: r.zone?.name || "N/A",
      agent: r.user?.full_name || "N/A",
    }));

  const exportHeaderDefs = [
    { text: "ID", type: "string", key: "id" },
    { text: "Référence", type: "string", key: "reference" },
    { text: "Matricule", type: "string", key: "matricule" },
    { text: "Type", type: "string", key: "type" },
    { text: "Montant", type: "int", key: "amount", align: "right" },
    { text: "Date Infraction", type: "string", key: "datetime_infraction" },
    { text: "Jours écoulés", type: "int", key: "days_passed", align: "right" },
    { text: "Statut", type: "string", key: "status" },
    { text: "Zone", type: "string", key: "zone" },
    { text: "Agent", type: "string", key: "agent" },
  ];

  const buildExportData = (baseUrl) => {
    const formattedData = buildExportRows(filteredData);
    return [
      [
        "Gestion des Infractions",
        "",
        `Date à partir de : ${dateRange?.[0]?.format("YYYY-MM-DD") || "N/A"}`,
        `Date jusqu'au : ${dateRange?.[1]?.format("YYYY-MM-DD") || "N/A"}`,
      ],
      [],
      [],
      user?.full_name,
      [`Total infractions: ${formattedData.length}`],
      [exportHeaderDefs],
      [formattedData],
      baseUrl,
      {
        total: formattedData.reduce((sum, it) => sum + parseFloat(it.amount || 0), 0),
        label: "Total Montant",
      },
    ];
  };

  const handleExcelClick = () => {
    if (filteredData.length === 0) return;
    setIsExporting(true);
    const timestamp = new Date().toISOString().replace(/:/g, "-");
    const filename = `infractions_${timestamp}.xlsx`;
    const data = buildExportData(window.location.origin);
    workerExcelRef.current.postMessage({ data, filename });
    setIsExporting(false);
  };

  const handlePdfClick = (operation) => {
    if (filteredData.length === 0) return;
    setIsExporting(true);
    const timestamp = new Date().toISOString().replace(/:/g, "-");
    const filename = `infractions_${timestamp}.pdf`;
    const data = buildExportData(window.location.origin);
    workerPdfRef.current.postMessage({ data, operation, filename });
  };

  const getPdfMenuItems = () => [
    {
      key: "download",
      label: "Télécharger",
      onClick: () => handlePdfClick("download"),
      disabled: isExporting,
    },
    {
      key: "open",
      label: "Ouvrir",
      onClick: () => handlePdfClick("open"),
      disabled: isExporting,
    },
  ];
  // --- End Export ---

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Référence",
      dataIndex: "reference",
      key: "reference",
      width: 120,
      render: (ref) => <Text copyable style={{ fontSize: 12 }}>{ref}</Text>,
    },
    {
      title: "Matricule",
      dataIndex: "matricule",
      key: "matricule",
      width: 130,
      render: (m) => <Tag color="blue">{m}</Tag>,
    },
    {
      title: "Type",
      key: "type",
      width: 180,
      ellipsis: true,
      render: (_, r) => r.type_infraction?.name || "N/A",
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      width: 100,
      sorter: (a, b) => a.amount - b.amount,
      render: (a) => <strong>{parseFloat(a || 0).toFixed(2)} MAD</strong>,
    },
    {
      title: "Date Infraction",
      dataIndex: "datetime_infraction",
      key: "datetime_infraction",
      width: 160,
      sorter: (a, b) => new Date(a.datetime_infraction) - new Date(b.datetime_infraction),
      render: (d) => d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "N/A",
    },
    {
      title: (
        <Tooltip title="Nombre de jours écoulés depuis la date d'infraction">
          <Space><ClockCircleOutlined /> Jours écoulés</Space>
        </Tooltip>
      ),
      key: "days_passed",
      width: 120,
      align: "center",
      sorter: (a, b) => getDaysPassed(a.datetime_infraction) - getDaysPassed(b.datetime_infraction),
      render: (_, r) => formatDaysPassed(getDaysPassed(r.datetime_infraction)),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (s) => <Tag color={colorStatusInfraction[s]}>{statusText(s)}</Tag>,
    },
    {
      title: "Zone",
      key: "zone",
      width: 120,
      ellipsis: true,
      render: (_, r) => r.zone?.name || "N/A",
    },
    {
      title: "Agent",
      key: "agent",
      width: 140,
      ellipsis: true,
      render: (_, r) => r.user?.full_name || "N/A",
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      fixed: "right",
      align: "center",
      render: (_, record) => {
        if (record.status === "pending") {
          return (
            <Popconfirm
              title="Bloquer cette infraction ?"
              onConfirm={() => handleBlock(record.id)}
              okText="Oui"
              cancelText="Non"
            >
              <Button
                danger
                icon={<LockOutlined />}
                size="small"
              >
                Bloquer
              </Button>
            </Popconfirm>
          );
        }
        if (record.status === "blocked") {
          return (
            <Popconfirm
              title="Débloquer cette infraction ?"
              onConfirm={() => handleUnblock(record.id)}
              okText="Oui"
              cancelText="Non"
            >
              <Button
                type="primary"
                icon={<UnlockOutlined />}
                size="small"
              >
                Débloquer
              </Button>
            </Popconfirm>
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: record.status !== "pending",
    }),
  };

  return (
    <div style={{ padding: "24px" }}>
      <Card bordered={false} style={{ marginBottom: 24 }}>
        <Title level={2}>
          <StopOutlined /> Gestion des Infractions
        </Title>
        <Text type="secondary">
          Consultez, filtrez et bloquez les infractions. La colonne "Jours écoulés" indique le temps passé depuis la date d'infraction.
        </Text>
      </Card>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Text strong style={{ display: "block", marginBottom: 4 }}>Période</Text>
            <RangePicker
              style={{ width: "100%" }}
              onChange={(dates) => setDateRange(dates || [])}
              value={dateRange.length === 2 ? dateRange : null}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col xs={12} md={4}>
            <Text strong style={{ display: "block", marginBottom: 4 }}>Jours min</Text>
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              placeholder="Min jours"
              value={minDaysPassed}
              onChange={setMinDaysPassed}
            />
          </Col>
          <Col xs={12} md={4}>
            <Text strong style={{ display: "block", marginBottom: 4 }}>Jours max</Text>
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              placeholder="Max jours"
              value={maxDaysPassed}
              onChange={setMaxDaysPassed}
            />
          </Col>
          <Col xs={24} md={4}>
            <Text strong style={{ display: "block", marginBottom: 4 }}>Statut</Text>
            <Select
              style={{ width: "100%" }}
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              placeholder="Tous"
            >
              <Option value="">Tous</Option>
              <Option value="pending">Non payé</Option>
              <Option value="paid">Payée</Option>
              <Option value="canceled">Annulée</Option>
              <Option value="blocked">Bloquée</Option>
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Text strong style={{ display: "block", marginBottom: 4 }}>Matricule</Text>
            <Input
              placeholder="Matricule..."
              prefix={<SearchOutlined />}
              value={searchMatricule}
              onChange={(e) => setSearchMatricule(e.target.value)}
              allowClear
            />
          </Col>
        </Row>
        <Row style={{ marginTop: 16 }}>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={fetchInfractions}
                loading={loading}
              >
                Rechercher
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  setDateRange([]);
                  setMinDaysPassed(null);
                  setMaxDaysPassed(null);
                  setStatusFilter("");
                  setSearchMatricule("");
                  fetchInfractions();
                }}
              >
                Réinitialiser
              </Button>
              {selectedRowKeys.length > 0 && (
                <Popconfirm
                  title={`Bloquer ${selectedRowKeys.length} infraction(s) sélectionnée(s) ?`}
                  onConfirm={handleBulkBlock}
                  okText="Oui, bloquer"
                  cancelText="Annuler"
                >
                  <Button
                    danger
                    icon={<LockOutlined />}
                    loading={bulkLoading}
                  >
                    Bloquer la sélection ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
          <Space>
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExcelClick}
              disabled={filteredData.length === 0 || isExporting}
            >
              Export Excel
            </Button>
            <Dropdown menu={{ items: getPdfMenuItems() }} disabled={isExporting}>
              <Button icon={<FilePdfOutlined />} disabled={isExporting}>
                Export PDF{" "}
                {isExporting ? "..." : <span style={{ marginLeft: 5 }}>▼</span>}
              </Button>
            </Dropdown>
          </Space>
        </div>
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1500 }}
          pagination={{
            total: filteredData.length,
            defaultPageSize: 20,
            showTotal: (total) => `Total: ${total} infractions`,
            showSizeChanger: true,
            pageSizeOptions: ["20", "50", "100", "200"],
          }}
          size="small"
        />
      </Card>
    </div>
  );
}

export default InfractionsManagement;
